import { useMemo, useRef, useState } from 'react';
import { useOrgFilter } from '@/store/defaults';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { departmentsApi, type Department } from '@/api/hr';
import { orgs } from '@/api/catalogs';
import { useConfirm } from '@/components/ConfirmProvider';
import ProjectSelect from '@/components/ProjectSelect';
import Drawer from '@/components/Drawer';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import type { ColDef } from 'ag-grid-community';
import ImportedBadge from '@/shared/components/ImportedBadge';
import InfiniteDocGrid, { TitleTotal } from '@/shared/components/InfiniteDocGrid';
import { useInfiniteGridDatasource } from '@/hooks/useInfiniteGridDatasource';
import { nextRefCode } from '@/utils/nextRefCode';

interface Editing {
  id?: string;
  organization_id: string;
  parent_id: string | null;
  code: string;
  name: string;
  default_direction_id: string | null;
}

export default function DepartmentsPage() {
  const confirm = useConfirm();
  const qc = useQueryClient();
  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  // UX β: фільтр для кнопки "+ Новий підрозділ" по payroll.manage.
  // Сам dropdown залишаємо повним (user може ПЕРЕГЛЯДАТИ підрозділи orgs
  // без права створювати у них) — фільтруємо лише creation-action.
  const { permissions_by_org, hasPermissionForCurrentOrg } = (() => {
    const result = usePermissionsByOrg('payroll.manage');
    const ids = new Set(result.availableOrgs.map((o) => o.id));
    return {
      permissions_by_org: result,
      hasPermissionForCurrentOrg: (id: string) => ids.has(id),
    };
  })();
  // За замовч. orgId='' → показуємо підрозділи ВСIХ юросіб (запит власника).
  // Фільтр по юрособі лишається опційним (dropdown з «Усі»).
  // Орг за замовчуванням з шапки (override при ручному виборі).
  const [orgId, setOrgId] = useOrgFilter();
  // 2026-08-23 (Норма #29): серверна пагінація + сортування.
  const refreshRef = useRef<(() => void) | null>(null);
  const { datasource, total } = useInfiniteGridDatasource<Department>(
    (limit, offset, sort) => departmentsApi.list({
      organization_id: orgId || undefined,
      sort_by: sort?.sort_by, sort_dir: sort?.sort_dir,
      limit, offset,
    }),
    [orgId],
  );
  const refreshList = () => {
    refreshRef.current?.();
    qc.invalidateQueries({ queryKey: ['departments'] });
  };
  // Резолв organization_id → назва (для колонки «Організація» у зведеному списку).
  const orgName = useMemo(
    () => new Map((orgList.data?.items ?? []).map((o) => [o.id, o.name])),
    [orgList.data],
  );

  const [editing, setEditing] = useState<Editing | null>(null);
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => editing!.id
      ? departmentsApi.update(editing!.id, {
          parent_id: editing!.parent_id, code: editing!.code, name: editing!.name,
          default_direction_id: editing!.default_direction_id,
        })
      : departmentsApi.create({
          organization_id: editing!.organization_id,
          parent_id: editing!.parent_id,
          code: editing!.code,
          name: editing!.name,
          default_direction_id: editing!.default_direction_id,
        }),
    onSuccess: () => {
      refreshList();
      setEditing(null);
    },
    onError: (e) => setError(extractError(e)),
  });
  const remove = useMutation({
    mutationFn: (id: string) => departmentsApi.remove(id),
    onSuccess: () => refreshList(),
  });

  const openEdit = (d: Department) => setEditing({
    id: d.id, organization_id: d.organization_id,
    parent_id: d.parent_id, code: d.code, name: d.name,
    default_direction_id: d.default_direction_id ?? null,
  });

  const cols = useMemo<ColDef<Department>[]>(() => [
    {
      headerName: '', width: 44, sortable: false,
      cellRenderer: (p: { data?: Department }) =>
        <ImportedBadge source={p.data?.external_source} />,
    },
    { field: 'code', headerName: 'Код', width: 120 },
    { field: 'name', headerName: 'Назва', flex: 1, minWidth: 240 },
    {
      // Зведений список усіх юросіб → показуємо приналежність.
      field: 'organization_id', headerName: 'Організація', width: 240,
      valueFormatter: (p) => p.value ? (orgName.get(p.value) ?? '—') : '',
    },
    // Колонку «Батьківський» прибрано (запит власника).
    // Iконки ✎/× прибрано — редагування через double-click по рядку.
  ], [orgName]);

  return (
    <div className="card">
      <div className="toolbar with-filters" style={{ gap: '0.4rem', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>Підрозділи<TitleTotal total={total} /></h2>
        <button className="primary" style={{ marginLeft: 'auto' }}
                disabled={!orgId || !hasPermissionForCurrentOrg(orgId)}
                title={
                  !orgId
                    ? 'Спершу оберіть юрособу'
                    : !hasPermissionForCurrentOrg(orgId)
                      ? `Немає прав створювати підрозділи у цій юрособі (потрібен payroll.manage). У вас є право у ${permissions_by_org.availableOrgs.length} з ${permissions_by_org.totalAccessibleOrgs} ваших організацій`
                      : undefined
                }
                onClick={async () => {
                  // Автонумерація коду (як у 1С) — коди юрособи тягнемо при відкритті.
                  const all = await departmentsApi.list({ organization_id: orgId, limit: 2000 });
                  setEditing({
                    organization_id: orgId, parent_id: null,
                    code: nextRefCode(all.items.map((d) => d.code)),
                    name: '', default_direction_id: null,
                  });
                }}>
          + Новий
        </button>
      </div>
      <div className="filters">
        <select value={orgId} onChange={(e) => setOrgId(e.target.value)}
                style={{ width: 140, flex: '0 0 auto' }}>
          <option value="">Усі юрособи</option>
          {orgList.data?.items.map((o) => (
            <option key={o.id} value={o.id}>{o.name} — {o.code}</option>
          ))}
        </select>
      </div>

      {editing && (
        <Drawer
          open={!!editing}
          title={editing.id ? `Підрозділ: ${editing.name || '—'}` : 'Новий підрозділ'}
          onClose={() => { setEditing(null); setError(null); }}
          footer={
            <>
              {editing.id && (
                <button className="danger"
                        onClick={() => confirm('Видалити підрозділ?').then((ok) => {
                          if (ok && editing.id) { remove.mutate(editing.id); setEditing(null); }
                        })}>
                  Видалити
                </button>
              )}
              <button onClick={() => { setEditing(null); setError(null); }}>Скасувати</button>
              <button className="primary" disabled={save.isPending}
                      onClick={() => save.mutate()}>
                Зберегти
              </button>
            </>
          }
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <label>Код
              <input placeholder="Код" value={editing.code}
                     onChange={(e) => setEditing({ ...editing, code: e.target.value })} />
            </label>
            <label>Назва
              <input placeholder="Назва підрозділу" value={editing.name}
                     onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
            </label>
            {/* Поле «Батьківський підрозділ» прибрано з форми (запит власника
                2026-06-15). parent_id лишається у моделі (nullable), просто не
                вводиться вручну — нові підрозділи створюються без батьківського. */}
            <label>Напрямок (за замовчуванням)
              <ProjectSelect
                value={editing.default_direction_id}
                onChange={(v) => setEditing({ ...editing, default_direction_id: v })}
                organizationId={editing.organization_id}
                kind="DIRECTION"
              />
            </label>
            {error && <div className="error">{error}</div>}
          </div>
        </Drawer>
      )}

      <InfiniteDocGrid<Department>
        datasource={datasource}
        columnDefs={cols}
        storageKey="departments-list"
        onRowDoubleClicked={openEdit}
        getRowStyle={(p) => p.data?.deletion_mark ? { opacity: 0.4 } : undefined}
        refreshRef={refreshRef}
      />
    </div>
  );
}

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as { response?: { data?: { detail?: string } } }).response;
    return r?.data?.detail ?? 'Error';
  }
  return String(e);
}
